import { FunctionComponent, useState, useEffect } from 'react';
import classNames from 'classnames';
import { ReactComponent as RocketIcon } from '../../../../../asset/images/icons/rocket.svg';
import { ReactComponent as AlertIcon } from '../../../../../asset/images/icons/alert.svg';
import styles from './AccountRocketFuel.module.scss';
import MainButton from '../../../../../components/buttons/MainButton/MainButton';
import TitleWithIcon from '../../../../../components/titles/TitleWithIcon/TitleWithIcon';
import Grid from '../../../../../components/blocks/Grid/Grid';
import PopUp from '../../../../../components/popups/PopUp/PopUp';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import api from '../../../../../api';
import { ApiResponseDTO } from '../../../../../dto/api';
import { AxiosError } from 'axios';
import { ProductDTO } from '../../../../../dto/product';
import ToastMessage from '../../../../../components/blocks/ToastMessage/ToastMessage';
import RoutesEnum from '../../../../../enums/routes';
import { toast } from 'react-toastify';
import ToastifyContent from '../../../../../components/blocks/ToastMessage/_parts/ToastifyContent/ToastifyContent';
import callToast from '../../../../../components/blocks/ToastMessage/_parts/callToast/callToast';
import PreloadProductCard from '../../../../../components/blocks/PreloadProductCard/PreloadProductCard';
import CSSTransitionWrapper from '../../../../wrappers/CSSTransitionWrapper/CSSTransitionWrapper';
import AstronomicLoader from '../../../../../components/blocks/AstronomicLoader/AstronomicLoader';
import useFormSubmitLoader from '../../../../../hooks/useFormSubmitLoader';
import { SESSION_STORAGE_KEYS } from '../../../../../enums/sessionStorageKeys';
import { getUserInfoAsyncThunk } from '../../../../../redux/slices/user/thunks';
import MetaTags from '../../../../../components/seo/MetaTags/MetaTags';
import RadioSwitch from '../../../../../components/inputs/RadioSwitch/RadioSwitch';
import PriceCard from '../../../../../components/blocks/PriceCard/PriceCard';
import ShowOnlyInSomeEnv from '../../../../../components/helpers/ShowOnlyInSomeEnv/ShowOnlyInSomeEnv';
import { sessionStorageGetItem, sessionStorageSetItem } from '../../../../../utils/webStorage/sessionStorageMethods';

const purchaseTypeData = [
  { label: 'One-Time Purchase', value: '1' },
  { label: 'Monthly Subscription', value: '2' },
];

interface AccountRocketFuelProps {}

const AccountRocketFuel: FunctionComponent<AccountRocketFuelProps> = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, handleFormSubmitLoader } = useFormSubmitLoader();

  const [productCardData, setProductCardData] = useState<ProductDTO[]>();
  const [creditsToBuy, setCreditsToBuy] = useState<ProductDTO>();

  const [buyCreditsPopUpActive, setSkipPopUpActive] = useState(false);
  const changeBuyCreditsPopUpActiveState = (state: boolean) => {
    setSkipPopUpActive(state);
  };

  const userId = useAppSelector((state) => state.user.user?.userId as string);
  const stripeCustomerId = useAppSelector((state) => state.user.user?.stripeCustomerId);
  const paymentMethodId = useAppSelector((state) => state.user.user?.stripeDefaultPaymentMethodId);
  const [purchaseType, setPurchaseType] = useState(purchaseTypeData[0].value);

  async function handleOnChargeCustomerForProduct(stripeProductId: string) {
    await handleFormSubmitLoader(async () => {
      try {
        const response = await api.Payment.chargeCustomerForProduct({
          stripeCustomerId: stripeCustomerId!,
          stripeProductId: stripeProductId,
          paymentMethodId: paymentMethodId!,
          userId: userId,
        });

        const responseData = response.data;

        if (responseData.success === true) {
          changeBuyCreditsPopUpActiveState(false);

          if (userId) {
            await dispatch(getUserInfoAsyncThunk(userId));
          }

          callToast({
            title: 'Payment successful!',
            children: (
              <>
                Your account has just been topped up with <b>Rocket Fuel Credits</b> 🚀
              </>
            ),
            variation: 'success',
          });
        } else {
          toast.error((props) => {
            const variation = props.toastProps.type;

            return <ToastifyContent variation={variation}>{responseData?.errorMessage}</ToastifyContent>;
          });

          console.error('Error:', response);
        }
      } catch (error) {
        const errorObj = error as AxiosError<ApiResponseDTO>;
        const errorData = errorObj.response?.data;

        toast.error((props) => {
          const variation = props.toastProps.type;

          return <ToastifyContent variation={variation}>{errorData?.errorMessage}</ToastifyContent>;
        });

        console.error('Error: ', error);
      }
    });
  }

  function calculateCreditsWithBonus(price, discount) {
    const creditsWithBonus = price + (price * discount) / 100;

    return creditsWithBonus.toLocaleString('en-US');
  }

  function calculateBonusCredits(price, discount) {
    const bonusCredits = (price * discount) / 100;

    return bonusCredits.toLocaleString('en-US');
  }

  let response;

  useEffect(() => {
    let cacheData = JSON.parse(sessionStorageGetItem(SESSION_STORAGE_KEYS.CACHE_ROCKET_FUEL_PRODUCTS)!);

    const getStripeProducts = async () => {
      try {
        const response = await api.Payment.getFuelBundles(userId);

        const responseData = response.data;

        if (responseData.success === true && responseData) {
          const correctedData = responseData.data?.map((item, i) => {
            return {
              ...item,
              suggested: i === 1 ? true : false,
              discount: item.bonusPercentage,
            };
          });

          setProductCardData(correctedData);
          sessionStorageSetItem(SESSION_STORAGE_KEYS.CACHE_ROCKET_FUEL_PRODUCTS, JSON.stringify(correctedData));
        } else {
          console.error('Error:', response.data.errors);
        }
      } catch (error) {
        const errorObj = error as AxiosError<ApiResponseDTO>;
        const errorData = errorObj.response?.data;

        if (errorData?.errorMessage) {
          alert(errorData.errorMessage);
        }

        console.error('Error: ', error);
      }
    };

    if (cacheData) {
      setProductCardData(cacheData);
    } else {
      getStripeProducts();
    }

    // NEED !!!!!! TO THINK HOW TO BYPASS RELOG AND CACHE WITH BOOSTED STATE
    // getStripeProducts();
  }, [response]);

  return (
    <div className={styles['rocket-fuel']}>
      <MetaTags title="Account - Rocket Fuel" />

      <div className={styles['credits']}>
        <TitleWithIcon
          className={styles['credits__title']}
          icon={
            <RocketIcon
              className={styles['credits__title-container__icon']}
              style={{ fill: 'var(--color-main)', width: '24px' }}
            />
          }
        >
          <h3 className={'text text--h3'}>Rocket Fuel Credits</h3>
        </TitleWithIcon>

        <p className={classNames(styles['credits__text-1'], 'text text--body-1')}>
          For every $100 you spend with us, you’ll earn $5 in Rocket Fuel Credits. These credits can be used across our
          entire platform, giving you the flexibility to save on any of our products and services.
        </p>

        <p className={classNames(styles['credits__text-2'], 'text text--body-1')}>
          <b>Bonus credits stack up</b> - for example, if you pay $500 using Rocket Fuel Credits, you'll get an extra
          $25 in bonus credits. Use your credits anytime to save on our services.
        </p>

        <ShowOnlyInSomeEnv showInWhatEnv={['development', 'staging']}>
          <RadioSwitch
            className={styles['credits__switch']}
            name="purchase_type"
            onChange={setPurchaseType}
            options={purchaseTypeData}
            selected={purchaseType}
          />
        </ShowOnlyInSomeEnv>

        <div className={styles['credits__switch-content']}>
          {purchaseType === '1' && (
            <div className={styles['credits__switch-content__part']}>
              <p className={classNames(styles['credits__text-3'], 'text text--h4')}>Buy Now, Save Later</p>

              <p className={classNames(styles['credits__text-4'], 'text text--body-2')}>
                Get ahead by purchasing Rocket Fuel Credits at a discounted rate for future use. These one-time
                purchases are perfect for those who want flexibility without committing to a subscription. Please note
                that all credit purchases are non-refundable.
              </p>

              <div className={classNames(styles['credits__grid'], styles['credits__grid--var-1'])}>
                {productCardData ? (
                  <Grid
                    itemGroup={productCardData.map((item, i) => (
                      <PriceCard
                        className={styles['credits__price-card']}
                        title={
                          <div className={styles['credits__price-card__title']}>
                            <p className={classNames(styles['credits__price-card__title__text-1'], 'text text--h4')}>
                              ${item.totalAmount.toLocaleString('en-US')}
                            </p>
                            <p
                              className={classNames(styles['credits__price-card__title__text-2'], 'text text--body-2')}
                            >
                              <b>in credits</b>
                            </p>
                          </div>
                        }
                        buttonText={`$${item.amountInDollars.toLocaleString('en-US')}`}
                        labelText={`${item.bonusPercentage}% more`}
                        onClick={() => {
                          changeBuyCreditsPopUpActiveState(true);
                          setCreditsToBuy(productCardData[i]);
                        }}
                        topBannerText={i === 1 ? 'Suggested for you' : ''}
                        key={i}
                      />
                    ))}
                  />
                ) : (
                  <Grid
                    itemGroup={Array.from({ length: 4 }).map((_, i) => (
                      <PreloadProductCard key={i} />
                    ))}
                  />
                )}
              </div>
            </div>
          )}

          <ShowOnlyInSomeEnv showInWhatEnv={['development', 'staging']}>
            {purchaseType === '2' && (
              <div className={styles['credits__switch-content__part']}>
                <p className={classNames(styles['credits__text-3'], 'text text--h4')}>Monthly Credits, Maximum Value</p>

                <p className={classNames(styles['credits__text-4'], 'text text--body-2')}>
                  Subscribe to one of our monthly plans and enjoy the convenience of automatically receiving Rocket Fuel
                  Credits every month. Choose the plan that best fits your needs and keep your growth fueled year-round!
                </p>

                <p className={classNames(styles['credits__text-5'], 'text text--caps')}>Available Subscriptions</p>

                <div className={classNames(styles['credits__grid'], styles['credits__grid--var-2'])}>
                  {productCardData ? (
                    <Grid
                      itemGroup={productCardData.map((item, i) => (
                        <PriceCard
                          className={styles['credits__price-card']}
                          title={
                            <div className={styles['credits__price-card__title']}>
                              <p className={classNames(styles['credits__price-card__title__text-1'], 'text text--h4')}>
                                $x
                              </p>
                              <p
                                className={classNames(
                                  styles['credits__price-card__title__text-2'],
                                  'text text--body-2'
                                )}
                              >
                                <b>in credits</b>
                              </p>
                            </div>
                          }
                          buttonText={'Subscribe'}
                          labelText={`x% more`}
                          onClick={() => {
                            changeBuyCreditsPopUpActiveState(true);
                            setCreditsToBuy(productCardData[i]);
                          }}
                          topBannerText={i === 1 ? 'Suggested for you' : ''}
                          key={i}
                        />
                      ))}
                    />
                  ) : (
                    <Grid
                      itemGroup={Array.from({ length: 4 }).map((_, i) => (
                        <PreloadProductCard key={i} />
                      ))}
                    />
                  )}
                </div>
              </div>
            )}
          </ShowOnlyInSomeEnv>
        </div>
      </div>

      {createPortal(
        <PopUp
          className={styles['buy-credits-pop-up']}
          active={buyCreditsPopUpActive}
          onPopUpStateChange={changeBuyCreditsPopUpActiveState}
          footer={
            <div className={styles['buy-credits-pop-up__footer-button-list']}>
              <div className={styles['buy-credits-pop-up__footer-button-list__item']}>
                <MainButton
                  visualType="white"
                  sizeType="medium"
                  onClick={() => {
                    changeBuyCreditsPopUpActiveState(false);
                  }}
                >
                  Cancel
                </MainButton>
              </div>

              {stripeCustomerId ? (
                <div className={styles['buy-credits-pop-up__footer-button-list__item']}>
                  <MainButton
                    visualType="main"
                    sizeType="medium"
                    onClick={() => handleOnChargeCustomerForProduct(creditsToBuy?.productId!)}
                    disabled={isLoading}
                  >
                    Confirm purchase
                  </MainButton>
                </div>
              ) : (
                <div className={styles['buy-credits-pop-up__footer-button-list__item']}>
                  <MainButton
                    visualType="main"
                    sizeType="medium"
                    onClick={() => navigate(`/${RoutesEnum.ACCOUNT}/${RoutesEnum.BILLING}`)}
                  >
                    Go to Billing
                  </MainButton>
                </div>
              )}
            </div>
          }
        >
          <CSSTransitionWrapper onEnter={isLoading} styleVariation="onForeground" className="">
            <AstronomicLoader variation="blurredBackground" />
          </CSSTransitionWrapper>

          <div className={styles['buy-credits-pop-up__icon-container']}>
            <RocketIcon style={{ fill: 'var(--color-main)', width: '24px' }} />
          </div>

          <p className={classNames(styles['buy-credits-pop-up__title'], 'text text--h4')}>Buy Rocket Fuel credits</p>

          <p className={classNames(styles['buy-credits-pop-up__text-3'], 'text text--body-2')}>
            Credit purchases are a one-time charge and non-refundable.
          </p>

          {!stripeCustomerId && (
            <ToastMessage
              className={styles['buy-credits-pop-up__alert']}
              visualStyle="1"
              customIcon={<AlertIcon />}
              title="No Credit Card"
            >
              <b>You don't have a credit card saved</b>. Before proceeding with the purchase, please go to Billing tab
              and register a credit card.
            </ToastMessage>
          )}

          <div className={styles['buy-credits-pop-up__info-container']}>
            <p
              className={classNames(styles['buy-credits-pop-up__text-1'], 'text text--h5')}
            >{`$${calculateCreditsWithBonus(creditsToBuy?.amountInDollars, creditsToBuy?.discount)} in credits`}</p>

            <div className={styles['buy-credits-pop-up__price']}>
              <p className="text text--caption">{`$${creditsToBuy?.amountInDollars.toLocaleString(
                'en-US'
              )} credits`}</p>
              <div className={styles['buy-credits-pop-up__price__dot']}></div>
              <p className="text text--caption">{`$${creditsToBuy?.bonusAmount} in bonus credits`}</p>
            </div>

            <p className={classNames(styles['buy-credits-pop-up__text-2'], 'text text--body-2 text--bold')}>
              {`Total: $${creditsToBuy?.amountInDollars.toLocaleString('en-US')}`}
            </p>
          </div>
        </PopUp>,
        document.getElementById('modal') as HTMLElement
      )}
    </div>
  );
};

export default AccountRocketFuel;
